import { useCallback, useEffect } from 'react';

import { isNumber } from 'lodash-es';
import { dollarsToCents } from 'utils';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { useCheckoutContext } from '../../hooks/use-checkout-context';
import { ITipSelection } from '../../types';

import { useTotalTipCalculations } from './use-total-tip-calculations';

/**
 * @name computeTipAmountCents
 * @returns {number} representing the tip amount in cents for
 *    either the base dollar amount OR the percentage amount
 */

export const TipAmounts = {
  PERCENT_AMOUNTS: [15, 18, 20],
  DOLLAR_AMOUNTS: [2, 3, 4],
  PERCENT_DEFAULT: 18,
  DOLLAR_DEFAULT: 3,
  STYLE_THRESHOLD: 1500,
  MAX_AMOUNT: 5000,
};

export const computeTipAmountCents = (baseAmount: number, percentAmount?: number) => {
  if (percentAmount) {
    return Math.round((percentAmount * baseAmount) / 100);
  }
  return dollarsToCents(baseAmount);
};

/**
 * Determines whether tip options should be displayed as a percentage instead of a fixed dollar amount.
 *
 * @param subTotalCents - The order subtotal in cents.
 * @param tipPercentThresholdCents - The threshold (in cents) for showing percentage-based tips.
 *   Comes from the TIP_PERCENT_THRESHOLD_CENTS feature flag (e.g., 1500).
 * @returns {boolean} - Returns true if tip options should be shown as a percentage, false for fixed dollar amounts.
 *
 * Logic:
 * - If the threshold is 0 or less, always show percentage options.
 * - If the subtotal exceeds the threshold, show percentage options.
 * - If the default dollar tip ($3) is greater than the subtotal (in dollars), show percentage options
 *   (to avoid suggesting a tip larger than the order).
 *
 * Examples:
 * - If subtotal is $20 (2000 cents) and threshold is 1500, returns true (show percentage).
 * - If subtotal is $10 (1000 cents) and threshold is 1500, returns true because $3 > $10.
 * - If subtotal is $20 (2000 cents) and threshold is 0, returns true.
 */
const shouldShowTipPercentage = (subTotalCents: number, tipPercentThresholdCents: number) =>
  tipPercentThresholdCents <= 0 ||
  subTotalCents > tipPercentThresholdCents ||
  TipAmounts.DOLLAR_DEFAULT > subTotalCents / 100;

const getTipAmount = (
  tipDetails: IUpdateTipAmountProps,
  tipSelection: ITipSelection,
  tipPercentThresholdCents: number,
  isShowingPercentage: boolean
) => {
  /** driver tip should be based on PRE-discounted subtotal */
  const baseAmountToTip = tipDetails.subTotalCents + tipDetails.otherDiscountAmount;

  if (tipDetails.newTipOption === 'dollar' && isNumber(tipDetails.newTipAmount)) {
    return computeTipAmountCents(tipDetails.newTipAmount);
  }
  if (tipDetails.newTipOption === 'percent' && tipDetails.newTipAmount) {
    return computeTipAmountCents(baseAmountToTip, tipDetails.newTipAmount);
  }
  if (tipSelection.isOtherSelected) {
    return computeTipAmountCents(tipSelection.otherAmount || 0);
  }
  if (isShowingPercentage) {
    return computeTipAmountCents(baseAmountToTip, tipSelection.percentAmount);
  }

  return computeTipAmountCents(tipSelection.dollarAmount);
};

export interface IUpdateTipAmountProps {
  subTotalCents: number;
  otherDiscountAmount: number;
  newTipOption?: string;
  newTipAmount?: number;
}

export const useTip = () => {
  const {
    tipAmount,
    tipSelection,
    tipIsLocked: isTipLocked,
    cart,
    checkoutPaymentMethodId,
    showReloadPaymentMethods,
    paymentMethods,
    reloadPaymentValues,
    setCheckoutState,
  } = useCheckoutContext();

  const tipPercentThresholdCents = useFlag(LaunchDarklyFlag.TIP_PERCENT_THRESHOLD_CENTS) || 0;
  const { subTotalCents = 0 } = cart || {};

  const { additionalDiscountAmount } = useTotalTipCalculations();

  const showTipPercentage = shouldShowTipPercentage(
    subTotalCents + additionalDiscountAmount,
    tipPercentThresholdCents
  );

  // TODO: there is logic that also checks for CA region, wondering what is most accurate
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);

  const selectedMethod = paymentMethods.find(
    method => method.accountIdentifier === checkoutPaymentMethodId
  );

  const isDisabled =
    (!!selectedMethod?.prepaid && !enableGiftCard) ||
    (showReloadPaymentMethods && !!paymentMethods.length && !reloadPaymentValues.saveCard);

  const setTipAmount = useCallback(
    (tipAmount: number) => {
      setCheckoutState(currState => ({ ...currState, tipAmount }));
    },
    [setCheckoutState]
  );

  const setTipSelection = (tipSelection: ITipSelection) => {
    setCheckoutState(currState => ({ ...currState, tipSelection }));
  };

  const setTipIsLocked = (tipIsLocked: boolean) => {
    setCheckoutState(currState => ({ ...currState, tipIsLocked }));
  };

  const updateTipAmount = useCallback(
    (tipDetails: IUpdateTipAmountProps) => {
      setTipAmount(
        getTipAmount(tipDetails, tipSelection, tipPercentThresholdCents, showTipPercentage)
      );
    },
    [tipSelection, tipPercentThresholdCents, showTipPercentage, setTipAmount]
  );

  const initializeTip = useCallback(() => {
    if (isDisabled || tipSelection.isOtherSelected || tipAmount > 0) {
      return;
    }

    updateTipAmount({
      subTotalCents,
      otherDiscountAmount: additionalDiscountAmount,
      newTipOption: showTipPercentage ? 'percent' : 'dollar',
    });
  }, [
    isDisabled,
    tipSelection.isOtherSelected,
    tipAmount,
    showTipPercentage,
    additionalDiscountAmount,
    updateTipAmount,
    subTotalCents,
  ]);

  useEffect(() => initializeTip(), [initializeTip]);

  return {
    isDisabled,
    showTipPercentage,
    isTipLocked,
    tipAmount,
    tipSelection,
    subTotalCents,
    additionalDiscountAmount,
    setTipIsLocked,
    setTipAmount,
    setTipSelection,
    updateTipAmount,
  };
};
